import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FileText, Plus, Search, Upload, Edit, Trash2, Eye, ChevronLeft, ChevronRight, CheckCircle, BookOpen, Users, MoreVertical, Clock, AlertCircle, Check, X } from 'lucide-react';
import AddDraftQuestionModal from './AddDraftQuestionModal';
import BulkUploadDraftModal from './BulkUploadDraftModal';
import EditDraftQuestionModal from './EditDraftQuestionModal';
import ViewDraftQuestionModal from './ViewDraftQuestionModal';
import ReviewDraftQuestionModal from './ReviewDraftQuestionModal';
import { CardLoadingSpinner } from '../ui/LoadingSpinner';
import { superAdminAPI } from '../../services/api';
import { showErrorToast, showSuccessToast, showWarningToast } from '../../utils/errorHandling';
import ErrorMessage from '../ui/ErrorMessage';
import { useAuth } from '../../contexts/AuthContext';

const DraftQuestionManagement = () => {
  // State management
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [operationLoading, setOperationLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [selectedCreatedBy, setSelectedCreatedBy] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(20);

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [viewingQuestion, setViewingQuestion] = useState(null);
  const [reviewingQuestion, setReviewingQuestion] = useState(null);

  // Filter options
  const [statusOptions, setStatusOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [difficultyOptions] = useState(['Easy', 'Medium', 'Hard']);

  // Selection
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Auth context
  const { user } = useAuth();
  const isContentUser = ['CONTENT_SUPERVISOR', 'CONTENT_GENERATOR', 'CONTENT_REVIEWER'].includes(user?.role);
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isContentGenerator = user?.role === 'CONTENT_GENERATOR';

  // Refs
  const searchInputRef = useRef(null);

  // Status options
  const statusOptionsData = [
    { value: 'DRAFT', label: 'Draft', color: 'bg-gray-100 text-gray-800' },
    { value: 'NEEDS_WORK', label: 'Needs Work', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'APPROVED', label: 'Approved', color: 'bg-green-100 text-green-800' },
    { value: 'PUBLISHED', label: 'Published', color: 'bg-blue-100 text-blue-800' }
  ];

  // Load questions
  const loadQuestions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        size: pageSize,
        sortBy: 'updatedAt',
        sortDir: 'desc'
      };

      // Add filters
      if (searchTerm) params.search = searchTerm;
      if (selectedStatus) params.status = selectedStatus;
      if (selectedCategory) params.subject = selectedCategory;
      if (selectedDifficulty) params.difficulty = selectedDifficulty;
      if (selectedCreatedBy) params.createdBy = selectedCreatedBy;

      console.log('Making API call with params:', params);
      const response = await superAdminAPI.getAllDraftQuestions(params);
      const data = response.data;
      console.log('API response:', data);

      const questionsArray = data.content || [];
      console.log('Questions array:', questionsArray, 'Is array:', Array.isArray(questionsArray));

      setQuestions(questionsArray);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);

    } catch (error) {
      console.error('Error loading draft questions:', error);
      setError('Failed to load draft questions. Please try again.');
      showErrorToast(error, 'Failed to load draft questions');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, selectedStatus, selectedCategory, selectedDifficulty, selectedCreatedBy]);

  // Load filter options
  const loadFilterOptions = useCallback(async () => {
    try {
      const [subjectsResponse] = await Promise.all([
        superAdminAPI.getDraftQuestionSubjects()
      ]);

      if (subjectsResponse.data) {
        setCategoryOptions(subjectsResponse.data);
      }

      setStatusOptions(statusOptionsData);

    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  }, []);

  // Effects
  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  // Handler functions
  const handleAddQuestion = async (questionData) => {
    try {
      setOperationLoading(true);
      await superAdminAPI.createDraftQuestion(questionData);
      await loadQuestions(); // Reload questions
      showSuccessToast('Draft question created successfully!');
    } catch (error) {
      console.error('Error creating draft question:', error);
      showErrorToast(error, 'Failed to create draft question');
      throw error; // Re-throw to let modal handle it
    } finally {
      setOperationLoading(false);
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setIsEditModalOpen(true);
  };

  const handleViewQuestion = (question) => {
    setViewingQuestion(question);
    setIsViewModalOpen(true);
  };

  const handleReviewQuestion = (question) => {
    setReviewingQuestion(question);
    setIsReviewModalOpen(true);
  };

  const handleUpdateQuestion = async (questionId, questionData) => {
    try {
      setOperationLoading(true);
      await superAdminAPI.updateDraftQuestion(questionId, questionData);
      await loadQuestions(); // Reload questions
      showSuccessToast('Draft question updated successfully!');
    } catch (error) {
      console.error('Error updating draft question:', error);
      showErrorToast(error, 'Failed to update draft question');
      throw error; // Re-throw to let modal handle it
    } finally {
      setOperationLoading(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this draft question?')) {
      return;
    }

    try {
      setOperationLoading(true);
      await superAdminAPI.deleteDraftQuestion(questionId);
      await loadQuestions(); // Reload questions
      showSuccessToast('Draft question deleted successfully!');
    } catch (error) {
      console.error('Error deleting draft question:', error);
      showErrorToast(error, 'Failed to delete draft question');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleReviewSubmit = async (questionId, reviewData) => {
    try {
      setOperationLoading(true);
      await superAdminAPI.reviewDraftQuestion(questionId, reviewData);
      await loadQuestions(); // Reload questions
      showSuccessToast('Question review submitted successfully!');
    } catch (error) {
      console.error('Error reviewing question:', error);
      showErrorToast(error, 'Failed to submit review');
      throw error;
    } finally {
      setOperationLoading(false);
    }
  };

  const handlePublishQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to publish this question? This action cannot be undone.')) {
      return;
    }

    try {
      setOperationLoading(true);
      await superAdminAPI.publishDraftQuestion(questionId);
      await loadQuestions(); // Reload questions
      showSuccessToast('Question published successfully!');
    } catch (error) {
      console.error('Error publishing question:', error);
      showErrorToast(error, 'Failed to publish question');
    } finally {
      setOperationLoading(false);
    }
  };

  // Search and filter handlers
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(0); // Reset to first page when searching
    loadQuestions();
  };

  const handleFilterChange = (filterType, value) => {
    setCurrentPage(0); // Reset to first page when filtering

    switch (filterType) {
      case 'status':
        setSelectedStatus(value);
        break;
      case 'category':
        setSelectedCategory(value);
        break;
      case 'difficulty':
        setSelectedDifficulty(value);
        break;
      case 'createdBy':
        setSelectedCreatedBy(value);
        break;
      default:
        break;
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedStatus('');
    setSelectedCategory('');
    setSelectedDifficulty('');
    setSelectedCreatedBy('');
    setCurrentPage(0);
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(questions.map(q => q.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectQuestion = (questionId) => {
    setSelectedQuestions(prev => {
      if (prev.includes(questionId)) {
        return prev.filter(id => id !== questionId);
      } else {
        return [...prev, questionId];
      }
    });
  };

  // Get status display info
  const getStatusInfo = (status) => {
    const statusInfo = statusOptionsData.find(s => s.value === status);
    return statusInfo || { label: status, color: 'bg-gray-100 text-gray-800' };
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'DRAFT':
        return <Clock className="h-4 w-4" />;
      case 'NEEDS_WORK':
        return <AlertCircle className="h-4 w-4" />;
      case 'APPROVED':
        return <Check className="h-4 w-4" />;
      case 'PUBLISHED':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  if (loading && questions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <CardLoadingSpinner />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={loadQuestions} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Draft Questions</h1>
          <p className="text-gray-600 mt-1">
            {isContentUser ? 'Manage your draft questions' : 'Review and manage draft questions'}
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {/* Add Question Button - Only for content users and super admins */}
          {(isContentUser || isSuperAdmin) && (
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              disabled={operationLoading}
            >
              <Plus className="h-4 w-4" />
              <span>Add Question</span>
            </button>
          )}

          {/* Bulk Upload Button - Only for content users and super admins */}
          {(isContentUser || isSuperAdmin) && (
            <button
              onClick={() => setIsBulkUploadModalOpen(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              disabled={operationLoading}
            >
              <Upload className="h-4 w-4" />
              <span>Bulk Upload</span>
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSearch} className="space-y-4">
          {/* Search Bar */}
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search questions by text, subject, or topic..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
              disabled={loading}
            >
              Search
            </button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Statuses</option>
                {statusOptions.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <select
                value={selectedCategory}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Subjects</option>
                {categoryOptions.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            {/* Difficulty Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
              <select
                value={selectedDifficulty}
                onChange={(e) => handleFilterChange('difficulty', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Difficulties</option>
                {difficultyOptions.map(difficulty => (
                  <option key={difficulty} value={difficulty}>
                    {difficulty}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                type="button"
                onClick={clearFilters}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <div>
          Showing {questions.length} of {totalElements} draft questions
          {selectedQuestions.length > 0 && (
            <span className="ml-2 text-blue-600">
              ({selectedQuestions.length} selected)
            </span>
          )}
        </div>

        {/* Bulk Actions */}
        {selectedQuestions.length > 0 && isSuperAdmin && (
          <div className="flex items-center space-x-2">
            <span className="text-gray-500">Bulk Actions:</span>
            <button
              onClick={() => {
                // Handle bulk approve
                console.log('Bulk approve:', selectedQuestions);
              }}
              className="text-green-600 hover:text-green-700 font-medium"
            >
              Approve Selected
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => {
                // Handle bulk publish
                console.log('Bulk publish:', selectedQuestions);
              }}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Publish Selected
            </button>
          </div>
        )}
      </div>

      {/* Questions Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <CardLoadingSpinner />
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No draft questions found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedStatus || selectedCategory || selectedDifficulty
                ? 'Try adjusting your search criteria or filters.'
                : 'Get started by creating your first draft question.'}
            </p>
            {(isContentGenerator || isSuperAdmin) && (
              <div className="mt-6">
                <button
                  onClick={() => setIsAddModalOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 mx-auto transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add First Question</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-12 px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Question
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Difficulty
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {questions.map((question) => {
                  const statusInfo = getStatusInfo(question.status);
                  const isSelected = selectedQuestions.includes(question.id);
                  const canEdit = isContentGenerator ? question.createdBy === user?.id : true;
                  const canReview = isSuperAdmin && (question.status === 'DRAFT' || question.status === 'NEEDS_WORK');
                  const canPublish = isSuperAdmin && question.status === 'APPROVED';

                  return (
                    <tr key={question.id} className={isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectQuestion(question.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-xs">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {question.questionText}
                          </p>
                          <p className="text-sm text-gray-500">
                            {question.options?.length || 0} options
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{question.subject}</div>
                        {question.topic && (
                          <div className="text-sm text-gray-500">{question.topic}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                          {getStatusIcon(question.status)}
                          <span className="ml-1">{statusInfo.label}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {question.difficultyLevel}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {question.createdBy}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(question.updatedAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {/* View Button */}
                          <button
                            onClick={() => handleViewQuestion(question)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded"
                            title="View Question"
                          >
                            <Eye className="h-4 w-4" />
                          </button>

                          {/* Edit Button */}
                          {canEdit && question.status !== 'PUBLISHED' && (
                            <button
                              onClick={() => handleEditQuestion(question)}
                              className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                              title="Edit Question"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}

                          {/* Review Button */}
                          {canReview && (
                            <button
                              onClick={() => handleReviewQuestion(question)}
                              className="text-green-600 hover:text-green-900 p-1 rounded"
                              title="Review Question"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          )}

                          {/* Publish Button */}
                          {canPublish && (
                            <button
                              onClick={() => handlePublishQuestion(question.id)}
                              className="text-purple-600 hover:text-purple-900 p-1 rounded"
                              title="Publish Question"
                            >
                              <BookOpen className="h-4 w-4" />
                            </button>
                          )}

                          {/* Delete Button */}
                          {canEdit && question.status !== 'PUBLISHED' && (
                            <button
                              onClick={() => handleDeleteQuestion(question.id)}
                              className="text-red-600 hover:text-red-900 p-1 rounded"
                              title="Delete Question"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Page {currentPage + 1} of {totalPages}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0}
              className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = Math.max(0, Math.min(totalPages - 5, currentPage - 2)) + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-2 rounded-lg border ${
                    pageNum === currentPage
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {pageNum + 1}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
              className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <AddDraftQuestionModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddQuestion}
      />

      <BulkUploadDraftModal
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
        onSuccess={loadQuestions}
      />

      <EditDraftQuestionModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingQuestion(null);
        }}
        question={editingQuestion}
        onSubmit={handleUpdateQuestion}
      />

      <ViewDraftQuestionModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setViewingQuestion(null);
        }}
        question={viewingQuestion}
      />

      <ReviewDraftQuestionModal
        isOpen={isReviewModalOpen}
        onClose={() => {
          setIsReviewModalOpen(false);
          setReviewingQuestion(null);
        }}
        question={reviewingQuestion}
        onSubmit={handleReviewSubmit}
      />
    </div>
  );
};

export default DraftQuestionManagement;
